@file:JsExport


import com.pincer.app.Application
import com.pincer.app.viewmodel.Node
import com.pincer.app.viewmodel.Rich
import com.pincer.app.viewmodel.TreeDiff
import com.pincer.core.Logger
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope

/*
What JavaScript sees of the app. The renderer engine (pincer/web) is written in TypeScript against the definitions
generated from this file, so a change here that breaks a renderer fails the build.

The tree crosses over as plain objects rather than Kotlin's own classes: arrays instead of lists, names instead of
enums, and each node's parent and children as compound keys. A renderer keeps the nodes it has been given by compound
key, and update() tells it which to add, redraw, reorder or remove (the first call adds everything).
*/
@OptIn(DelicateCoroutinesApi::class)
class PincerApp {

    private val application = Application(coroutineScope = GlobalScope, log = Logger())

    // Starts the app, at a pincer if the page was opened with one (#PIDPIDPIDPID)
    fun start(initialLocation: String) = application.start(initialLocation = initialLocation, fresh = false)

    // Called whenever something changes (the person's own actions, pushes from the server); then call update()
    fun onChange(listener: () -> Unit) = application.listenToChanges(listener)

    // Rebuilds the tree, and says what changed since the last call
    fun update(): ViewDiff = viewDiff(application.update())

    // Does what the control at this compound key does, with this value. Throws if it isn't on screen or the value doesn't fit.
    fun perform(compoundKey: String, value: String?) { application.perform(compoundKey, value) }

    // Where the person is (a pid), for the page's address
    fun location(): String? = application.location()

}

class ViewDiff(
    val added:       Array<ViewNode>,  // parents before their children
    val removed:     Array<String>,    // compound keys, descendants included
    val changed:     Array<ViewNode>,  // the node's own fields changed
    val reordered:   Array<ViewNode>,  // same children, different order: see childKeys
    val description: String,           // one line per change, for logs
) {
    fun isEmpty() = added.isEmpty() && removed.isEmpty() && changed.isEmpty() && reordered.isEmpty()
}

/*
One node of the tree (see pincer/app/.../viewmodel/Node.kt for what each part means). Names are the Kotlin enum
names: showAs Loading, Warning, Problem or HelpText; control Button, Input, Multiline, Select or Checkbox;
prominence Primary, Normal or Secondary.
*/
class ViewNode(
    val key:         String,
    val compoundKey: String,
    val parentKey:   String?,
    val previousKey: String?,          // the sibling before this one, if any
    val childKeys:   Array<String>,
    val depth:       Int,              // the root is 0
    val wording:     String,           // always text, never HTML: it's other people's words
    val showAs:      String?,
    val control:     String?,
    val choices:     Array<Choice>?,   // for a select
    val action:      String?,          // what the control does, for developers (e.g. "SetAnswer(questionId=Q1)")
    val prominence:  String,
    val summary:     String?,
    val open:        Boolean?,         // null: doesn't open and close
    val isOpener:    Boolean,          // the checkbox that opens and closes its parent
    val rich:        RichView?,
)

class Choice(
    val value:   String,
    val wording: String,
)

// A richer way to show a node; its plain version is always there too (see Rich.kt)
class RichView(
    val type:   String,                // AvailabilityGrid or DateTimePicker
    val zone:   String?,               // AvailabilityGrid: the time zone the slots are in
    val slots:  Array<GridSlotView>?,  // AvailabilityGrid
    val format: String?,               // DateTimePicker: DATE, TIME or DATETIME
)

class GridSlotView(
    val childKey:  String,             // compound key of the child with the plain version
    val start:     String,
    val minutes:   Int,
    val available: Int,
    val undecided: Int,
)

private fun viewDiff(diff: TreeDiff) = ViewDiff(
    added       = diff.added.map(::viewNode).toTypedArray(),
    removed     = diff.removed.toTypedArray(),
    changed     = diff.changed.map(::viewNode).toTypedArray(),
    reordered   = diff.reordered.map(::viewNode).toTypedArray(),
    description = diff.describe(),
)

private fun viewNode(node: Node): ViewNode {
    var depth = 0
    var up = node.parent
    while (up != null) { depth++; up = up.parent }
    return ViewNode(
        key         = node.key,
        compoundKey = node.compoundKey(),
        parentKey   = node.parent?.compoundKey(),
        previousKey = node.getPreviousSibling()?.compoundKey(),
        childKeys   = node.children.map { it.compoundKey() }.toTypedArray(),
        depth       = depth,
        wording     = node.wording,
        showAs      = node.showAs?.name,
        control     = node.controlType?.name,
        choices     = node.controlChoices?.map { Choice(value = it.key, wording = it.value) }?.toTypedArray(),
        action      = node.action?.toString(),
        prominence  = node.prominence.name,
        summary     = node.summary,
        open        = node.open,
        isOpener    = node.parent?.openControl() === node,
        rich        = node.rich?.let { rich -> when (rich) {
            is Rich.AvailabilityGrid -> RichView(type = rich.type(), zone = rich.zone, format = null, slots = rich.slots.map {
                GridSlotView(childKey = node.compoundKeyOf(it.key), start = it.start, minutes = it.minutes, available = it.available, undecided = it.undecided)
            }.toTypedArray())
            is Rich.DateTimePicker   -> RichView(type = rich.type(), zone = null, format = rich.format.name, slots = null)
        } },
    )
}
